/**
 * SOLID Review
 * S — Good. One concern: the entry funnel — naming a guest, checking a table code, moving between
 *     screens, dismissing a join error.
 * O — Good. A new funnel step is one more decision.
 * L — N/A — functions.
 * I — Good. One function per decision on the contract.
 * D — Strong. The session draft and the schemas' types.
 */
import { decided, refuse, REFUSAL, type IEntityDecision, type TDecisionResult } from '../shared';
import { draftOf, settleSession, withPlayer, type IConfigSettleSession } from './sessionDraft';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityClientGuest, IEntityClientPlayer, IStateClientSession, TClientScreen } from '../../ports/schemas/ClientSession';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type {
  ICommandCreateGuest, ICommandGuestCreated, ICommandTableCode, ICommandTableCodeChecked, ICommandNavigate,
  IPayloadGuest, IPayloadCodeLookup,
} from '../../ports/schemas/SessionMoves';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';

/**
 * THE ENTRY FUNNEL (frontend strict-ports plan, FE2, 2026-10-03): IdentityModal's validation and its
 * write of the new guest, EnterCodeScreen's two outcomes, and every screen change the funnel's buttons
 * made with `setScreen`. The messages are the screens', word for word.
 */

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

/** IdentityModal's limit on a display name. */
const MAX_NAME_LENGTH = 32;
const APPLIED: IEntityTableAck = { applied: true };

/** Where a funnel step leads: a player with an identity skips the guest/login choice. */
function onwardScreen(session: IStateClientSession): TClientScreen {
  return session.account.kind === 'anonymous' ? 'GUEST_CHOICE' : 'GAME';
}

/** A guest's name, checked before anything is created. */
export function decidePlanGuest(_input: IPropsClientInput, command: ICommandCreateGuest): TSession<IPayloadGuest> {
  const trimmed = command.displayName.trim();
  if (trimmed.length === 0) return refuse('Please enter a name.', REFUSAL.INVALID_NAME);
  if (trimmed.length > MAX_NAME_LENGTH) return refuse(`Name must be ${MAX_NAME_LENGTH} characters or fewer.`, REFUSAL.INVALID_NAME);
  const request: IPayloadGuest = { displayName: trimmed };
  const nothing: IPropsClientChanges = {};
  return decided(nothing, request);
}

/**
 * The server created the player: the tab is seated as them. Without a registered account the tab is
 * that guest from now on, kept across a reload; a registered account keeps its own identity.
 */
export function decideGuestCreated(input: IPropsClientInput, command: ICommandGuestCreated): TSession<IEntityTableAck> {
  const { session } = input;
  const { player } = command;
  const next = draftOf(input);
  const seat: IEntityClientPlayer = { playerId: player.id, displayName: player.displayName, matchCredits: player.matchCredits, isAdmin: false };
  withPlayer(next, seat);
  let keep: IConfigSettleSession = {};
  if (session.account.kind !== 'registered') {
    next.session.account = { kind: 'guest', playerId: player.id, displayName: player.displayName, email: null, matchCredits: session.account.matchCredits };
    const guest: IEntityClientGuest = { playerId: player.id, displayName: player.displayName };
    keep = { guest };
  }
  return decided(settleSession(input, next, keep), APPLIED);
}

/**
 * EnterCodeScreen's Continue. No code: host a new table — straight to the game for a known player, to
 * the guest choice otherwise. A code: look it up first.
 */
export function decidePlanTableCode(input: IPropsClientInput, command: ICommandTableCode): TSession<IPayloadCodeLookup> {
  const code = command.code.trim().toUpperCase();
  if (code.length > 0) {
    const lookup: IPayloadCodeLookup = { code };
    const nothing: IPropsClientChanges = {};
    return decided(nothing, lookup);
  }
  const next = draftOf(input);
  next.session.pendingRoomCode = null;
  next.session.hostOnEntry = true;
  next.session.screen = onwardScreen(input.session);
  const none: IPayloadCodeLookup = { code: null };
  return decided(settleSession(input, next), none);
}

/** The code's lookup: no such table is refused with the screen's message; a table is joined, not hosted. */
export function decideTableCodeChecked(input: IPropsClientInput, command: ICommandTableCodeChecked): TSession<IEntityTableAck> {
  if (!command.found) return refuse('No game table found for that code. Check it and try again.', REFUSAL.NOT_FOUND);
  const next = draftOf(input);
  next.session.hostOnEntry = false;
  next.session.pendingRoomCode = command.code;
  next.session.pendingJoinError = null;
  next.session.screen = onwardScreen(input.session);
  return decided(settleSession(input, next), APPLIED);
}

/**
 * A funnel button's screen change. Leaving the game screen also ends a failed auto-host's panel —
 * "Choose manually" is this — so a later visit does not show the old error.
 */
export function decideNavigate(input: IPropsClientInput, command: ICommandNavigate): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.screen = command.screen;
  if (command.screen !== 'GAME') {
    next.session.hosting = false;
    next.session.hostError = null;
  }
  return decided(settleSession(input, next), APPLIED);
}

/** The welcome screen's ✕ on a join error. */
export function decideDismissJoinError(input: IPropsClientInput, _command: ICommandNothing): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.pendingJoinError = null;
  return decided(settleSession(input, next), APPLIED);
}
